import { ReactElement, ReactFragment, useMemo } from 'react'
import { Heading, SVGs, Text } from '@raskrask-dk/rr-components'
import { useTranslation } from 'react-i18next'
import { PageTemplate } from '../chemistry/skeleton'
import { ServicePicker, ServiceTexts } from './styles'
import { RenderIf } from '../chemistry/higherOrderComponents/renderIf'
import { KlippeKortTemplateProps } from './props'
import {
    GoalComponent,
    GoToServicePicker,
    KlippeCardButton,
    PartnerPickerComponent,
    ServicePickerComponent,
    ServiceTypesComponent,
} from '../chemistry/calendarComponents/components'
import { mapPackageCardInterfaceToPackageModal } from './mappers'
import { PartnerInfoModal } from '../chemistry/klippekortComponents/partnerInfoModal'
import { PartnerPackageModal } from '../chemistry/klippekortComponents/partnerPackageModal'
import { KlippekortPackageModal } from '../chemistry/klippekortComponents/packageModal'
import { useAppSelector } from '../../redux/hooks'
import { getIsMobile } from '../../redux/reducers/appSettings'

export const KlippeKortTemplate = ({
    basisType,
    goalHandler,
    serviceHandler,
    partnerHandler,
    packageHandler,
    goBack,
    goNext,
    isCompanyBooking,

    isModalOpen,
    modalType,
    setIsModalOpen,
    setModalType,
    servicePickerMinHeight,
    servicePickerRef,
    isServiceTypePickerOpen,

    onGoalClick,
    onServiceClick,
    onClickGoToServicePicker,
    onClickKlippeCardButton,
    onClickPartner,
}: KlippeKortTemplateProps): ReactElement => {
    const { t } = useTranslation()
    const isMobile = useAppSelector((state) => getIsMobile(state))

    const basisTypeId = basisType.id

    const [selectedGoal, goals] = goalHandler
    const [selectedService, services] = serviceHandler
    const [
        selectedPartner,
        setPartner,
        inspectedPartner,
        setInspectedPartner,
        partners,
    ] = partnerHandler
    const [
        packageTemplate,
        setPackageTemplate,
        inspectedPackage,
        setInspectedPackage,
        packageCards,
        isAggregated,
    ] = packageHandler

    const goalsComponent: ReactFragment = GoalComponent(
        goals,
        selectedGoal,
        onGoalClick,
        basisTypeId
    )
    const servicesTypesComponent: ReactFragment = ServiceTypesComponent(
        services,
        selectedService,
        onServiceClick,
        basisTypeId
    )

    const goToServicePicker: ReactFragment = GoToServicePicker(
        selectedGoal,
        selectedService,
        onClickGoToServicePicker
    )

    const klippecardButton: ReactFragment = KlippeCardButton(
        selectedGoal,
        selectedService,
        onClickKlippeCardButton,
        basisTypeId,
        false,
        true
    )

    const partnerPickerComponent: React.ReactFragment = PartnerPickerComponent(
        selectedService,
        basisType,
        partners,
        selectedPartner,
        onClickPartner,
        isMobile,
        isCompanyBooking,
        undefined,
        packageTemplate
    )

    const servicePicker: ReactFragment = ServicePickerComponent(
        goalsComponent,
        servicesTypesComponent,
        klippecardButton
    )

    const modalComponent = useMemo(() => {
        const icon =
            basisType.name === 'Massage'
                ? SVGs.MassageIcon
                : basisType.name === 'Training'
                ? SVGs.TrainingIcon
                : SVGs.YogaIcon

        const aggregatedPriceLabel = t('klippekort.price.labelAggregated')
        const nonAggregatedPriceLabel = t('klippekort.price.label')
        const aggregatedSaveLabel = t('klippekort.price.saveLabelaggregated')
        const nonAggregatedSaveLabel = t('klippekort.price.saveLabel')

        const priceLabel = isAggregated
            ? aggregatedPriceLabel
            : nonAggregatedPriceLabel

        const saveLabel = isAggregated
            ? aggregatedSaveLabel
            : nonAggregatedSaveLabel

        const serviceNameTranslated = t(`klippeCard_Label${selectedGoal.id}`)

        //PackgeCard -> IPackgeCard (RR)
        const packageBFs = packageCards.map((pc) =>
            mapPackageCardInterfaceToPackageModal(
                pc,
                inspectedPackage,
                setInspectedPackage,
                icon,
                priceLabel,
                saveLabel,
                serviceNameTranslated
            )
        )
        const packageBF = mapPackageCardInterfaceToPackageModal(
            inspectedPackage,
            inspectedPackage,
            setInspectedPackage,
            icon,
            priceLabel,
            saveLabel,
            serviceNameTranslated
        )

        if (!isModalOpen) return <></>
        switch (modalType) {
            case 'Packages':
                return (
                    <KlippekortPackageModal
                        goBack={goBack}
                        setPackageTemplate={setPackageTemplate}
                        setIsModalOpen={setIsModalOpen}
                        setModalType={setModalType}
                        inspectedPackage={inspectedPackage}
                        selectedGoal={selectedGoal}
                        packageBFs={packageBFs}
                        packageBF={packageBF}
                    />
                )
            case 'PartnerInfo':
                return (
                    <PartnerInfoModal
                        setInspectedPartner={setInspectedPartner}
                        setIsModalOpen={setIsModalOpen}
                        setPartner={setPartner}
                        goNext={goNext}
                        selectedGoal={selectedGoal}
                        inspectedPartner={inspectedPartner}
                        basisType={basisType}
                    />
                )
            case 'PartnerAndPackages':
                return (
                    <PartnerPackageModal
                        selectedPartner={selectedPartner}
                        partners={partners}
                        selectedGoal={selectedGoal}
                        goBack={goBack}
                        goNext={goNext}
                        setPackageTemplate={setPackageTemplate}
                        packageBFs={packageBFs}
                        packageBF={packageBF}
                        inspectedPackage={inspectedPackage}
                    />
                )
            default:
                throw new Error('not implemented modal type')
        }
    }, [
        basisType,
        goBack,
        goNext,
        inspectedPackage,
        inspectedPartner,
        isModalOpen,
        modalType,
        partners,
        selectedGoal,
        selectedPartner,
        setInspectedPartner,
        setIsModalOpen,
        setModalType,
        setPackageTemplate,
        setPartner,
        isAggregated,
        packageCards,
        setInspectedPackage,
        t,
    ])

    return isMobile ? (
        <PageTemplate
            Top={
                <ServicePicker
                    css={{
                        minH: servicePickerMinHeight,
                    }}
                    ref={servicePickerRef}
                >
                    <RenderIf condition={isServiceTypePickerOpen}>
                        <Heading bold primary size={5}>
                            {t(basisType.t.name)}
                        </Heading>
                        {servicePicker}
                        {selectedService && (
                            <ServiceTexts>
                                <Text bold primary>
                                    {t(
                                        `common:servicetypes.${selectedService.id}.info.title`
                                    )}
                                </Text>
                                <Text primary>
                                    {t(
                                        `common:servicetypes.${selectedService.id}.info.text`
                                    )}
                                </Text>
                            </ServiceTexts>
                        )}
                    </RenderIf>
                    <RenderIf condition={!isServiceTypePickerOpen}>
                        {goToServicePicker}
                    </RenderIf>
                </ServicePicker>
            }
            Body={
                <RenderIf condition={!isServiceTypePickerOpen}>
                    {partnerPickerComponent}
                </RenderIf>
            }
            Modal={modalComponent}
        />
    ) : (
        <PageTemplate
            Top={
                <ServicePicker>
                    <Heading bold primary size={4}>
                        {t(basisType.t.name)}
                    </Heading>
                    {servicePicker}
                </ServicePicker>
            }
            Body={partnerPickerComponent}
            Modal={modalComponent}
        />
    )
}
